import { useEffect, useRef, useState, useCallback } from 'react';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';
import { notification } from 'antd';
import { FirebaseApp, FirebaseFunction } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY || '';

// Track token yang sudah diregister per session (survive across re-renders & re-login)
// Key: userId, Value: token string
const registeredTokens = {};

/**
 * Hook untuk Firebase Cloud Messaging (FCM).
 *
 * - Cek permission state tanpa trigger prompt (aman di iOS)
 * - Jika sudah granted → auto-register token
 * - Jika belum → return needsPermission=true agar UI bisa tampilkan banner
 *   dengan tombol yang di-tap user (user gesture, wajib untuk iOS PWA)
 * - Listen foreground messages dan tampilkan via Ant Design notification
 *
 * @returns {{ needsPermission: boolean, requestPermission: () => Promise<void> }}
 */
export function useNotification(userId) {
	const unsubscribeRef = useRef(null);
	const [needsPermission, setNeedsPermission] = useState(false);

	const registerTokenAndListen = useCallback(async () => {
		if (!userId || !VAPID_KEY) return;

		try {
			const messaging = getMessaging(FirebaseApp);
			const swRegistration = await navigator.serviceWorker.ready;
			const token = await getToken(messaging, {
				vapidKey: VAPID_KEY,
				serviceWorkerRegistration: swRegistration,
			});

			// Jika token sama dengan yang sudah diregister untuk userId ini, skip
			if (token && registeredTokens[userId] === token) {
				// Token sudah diregister di session ini, setup listener saja
				if (!unsubscribeRef.current) {
					unsubscribeRef.current = onMessage(messaging, (payload) => {
						const { title, body } = payload.notification || {};
						if (!title) return;
						notification.open({
							message: title,
							description: body,
							placement: 'topRight',
							duration: 5,
						});
					});
				}
				return;
			}

			// Token baru atau belum diregister — kirim ke server
			if (token) {
				const registerFcmToken = httpsCallable(
					FirebaseFunction,
					'registerFcmToken'
				);
				await registerFcmToken({ token, platform: 'web' });
				registeredTokens[userId] = token;
			}

			// Setup foreground listener
			if (!unsubscribeRef.current) {
				unsubscribeRef.current = onMessage(messaging, (payload) => {
					const { title, body } = payload.notification || {};
					if (!title) return;
					notification.open({
						message: title,
						description: body,
						placement: 'topRight',
						duration: 5,
					});
				});
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn('[FCM] Token registration failed:', err.message);
		}
	}, [userId]);

	useEffect(() => {
		if (!userId || !VAPID_KEY) {
			// Hapus tracking untuk userId yang logout agar re-login force register ulang
			Object.keys(registeredTokens).forEach((key) => {
				delete registeredTokens[key];
			});
			return undefined;
		}

		// Notification API tidak tersedia (bukan PWA / browser tidak support)
		if (typeof Notification === 'undefined') return undefined;

		const setup = async () => {
			try {
				const currentPermission = Notification.permission;

				if (currentPermission === 'granted') {
					setNeedsPermission(false);
					await registerTokenAndListen();
				} else if (currentPermission === 'default') {
					setNeedsPermission(true);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn('[FCM] Setup failed:', err.message);
			}
		};

		setup();

		return () => {
			if (unsubscribeRef.current) {
				unsubscribeRef.current();
				unsubscribeRef.current = null;
			}
		};
	}, [userId, registerTokenAndListen]);

	// Dipanggil dari UI button (user gesture) — wajib untuk iOS PWA
	const requestPermission = useCallback(async () => {
		try {
			const permission = await Notification.requestPermission();
			if (permission === 'granted') {
				setNeedsPermission(false);
				await registerTokenAndListen();
			} else {
				setNeedsPermission(false);
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn('[FCM] Permission request failed:', err.message);
			setNeedsPermission(false);
		}
	}, [registerTokenAndListen]);

	return { needsPermission, requestPermission };
}
